import {isDefined} from 'Quoting/utils/utilities';
import {Map as ImmutableMap, fromJS} from 'immutable';
import {getMoneyObject} from '../PlanTemplates/ACC/GAI7';
import {isStateApplicableForHSS} from '../PlanTemplates/ACC/GAI7Utilities';
import { GPLUCoverageCode } from '../PlanTemplates/GWL/gpluConstants';

const PortabilityAppliedIndicator = 'HIBenefitStructure.isPortabilityApplied';

export const updateDefaultValuesMapForStateAndPlan = (
  defaultValuesMap: ImmutableMap<string, any> | undefined,
  situsStateCd?: string,
  planCd?: string,
  isPortabilityApplied?: boolean
) => {
  switch (planCd) {
    case 'GIM2':
      return updatedDefaultsForGIM2(defaultValuesMap, situsStateCd, isPortabilityApplied);
    case 'GHI3':
      return updatedDefaultsForGHI3(defaultValuesMap, situsStateCd, isPortabilityApplied);
    case 'GAI7':
      return updatedDefaultsForGAI7(defaultValuesMap, situsStateCd, isPortabilityApplied);
    case 'GVA6':
      return updatedDefaultsForGVA6(defaultValuesMap, situsStateCd, isPortabilityApplied);
    // GPLU
    case 'Plan1':
      return updatedDefaultsForGPLU(defaultValuesMap, situsStateCd);
    default:
      return defaultValuesMap ?? ImmutableMap();
  }
};

const updatedDefaultsForGIM2 = (
  defaultValuesMap: ImmutableMap<string, any> | undefined,
  situsStateCd?: string,
  isPortabilityApplied?: boolean
) => {
  if (situsStateCd === 'NJ') {
    return isDefined(defaultValuesMap) && ImmutableMap.isMap(defaultValuesMap)
      ? defaultValuesMap.withMutations(defaultValues => {
          const isHosConfinementApplied = Boolean(defaultValues.get('HIBenefitStructure.isHosConfinementApplied'));
          const isICUConfinementApplied = Boolean(defaultValues.get('HIBenefitStructure.isICUConfinementApplied'));
          const icuConfinementBenefitAmount = defaultValues.get('HIICUConfinementBenefit.benefitAmount')?.get('amount');
          const hosConfinementBenefitAmount = defaultValues.get('HIHosConfinementBenefit.benefitAmount').get('amount');

          if (
            (isHosConfinementApplied &&
              isICUConfinementApplied &&
              hosConfinementBenefitAmount + icuConfinementBenefitAmount < 250) ||
            (isHosConfinementApplied && !isICUConfinementApplied && hosConfinementBenefitAmount < 250)
          ) {
            defaultValues.set(
              'HIHosAdmissionBenefit.benefitAmount',
              fromJS({_type: 'Money', amount: 400, currency: 'USD'})
            );
            defaultValues.set('HIBenefitStructure.isHosAdmissionApplied', true);
          } else {
            defaultValues.set('HIBenefitStructure.isHosAdmissionApplied', false);
          }
          defaultValues.set(PortabilityAppliedIndicator, isPortabilityApplied);
        })
      : ImmutableMap<string, any>();
  }
  return defaultValuesMap ?? ImmutableMap<string, any>();
};

const updatedDefaultsForGHI3 = (
  defaultValuesMap: ImmutableMap<string, any> | undefined,
  situsStateCd?: string,
  isPortabilityApplied?: boolean
) => {
  return isDefined(defaultValuesMap) && ImmutableMap.isMap(defaultValuesMap)
    ? defaultValuesMap.withMutations(defaultValues => {
        defaultValues.set('HIBenefitStructure.isHospitalConfinementRoutineNewbornApplied', false);
        defaultValues.set('HIBenefitStructure.isHospitalAdmissionRoutineNewbornApplied', false);
        defaultValues.set('HIBenefitStructure.isICUConfinementApplied', true);
        defaultValues.set('HIBenefitStructure.isStepDownIntensiveCareAdmissionApplied', true);
        defaultValues.set('HIICUConfinementBenefit.paymentFrequencyLimitCd', '1/Year');

        // As per NJ state requirements
        if (situsStateCd === 'NJ') {
          defaultValues.set(
            'HIHosAdmissionBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 400, currency: 'USD'})
          );

          defaultValues.set(
            'HIHosConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 100, currency: 'USD'})
          );

          defaultValues.set(
            'HIICUDailyConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 200, currency: 'USD'})
          );

          defaultValues.set(
            'HIICUDailyStepDownConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 150, currency: 'USD'})
          );

          defaultValues.set('HIBenefitStructure.isICUConfinementApplied', false);
          defaultValues.set('HIBenefitStructure.isStepDownIntensiveCareAdmissionApplied', false);
          defaultValues.remove('HIICUConfinementBenefit.paymentFrequencyLimitCd');
        } else {
          defaultValues.set(
            'HIHosAdmissionBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 500, currency: 'USD'})
          );

          defaultValues.set(
            'HIHosConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 100, currency: 'USD'})
          );

          defaultValues.set(
            'HIICUConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 1000, currency: 'USD'})
          );
          defaultValues.set(
            'HIICUStepDownAdmissionBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 1.5 * 500, currency: 'USD'})
          );
          defaultValues.set(
            'HIICUDailyConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 2 * 100, currency: 'USD'})
          );
          defaultValues.set(
            'HIICUDailyStepDownConfinementBenefit.benefitAmount',
            fromJS({_type: 'Money', amount: 1.5 * 100, currency: 'USD'})
          );

          defaultValues.set('HIObservationUnit.benefitAmount', fromJS({_type: 'Money', amount: 100, currency: 'USD'}));

          if (['TX', 'AR', 'IN'].includes(situsStateCd)) {
            defaultValues.set('HIBenefitStructure.isHospitalConfinementRoutineNewbornApplied', true);
            defaultValues.set('HIBenefitStructure.isHospitalAdmissionRoutineNewbornApplied', true);
            defaultValues.set(
              'HIHosAdmissionRoutineNewBornBenefit.benefitAmount',
              fromJS({_type: 'Money', amount: 50, currency: 'USD'})
            );
            defaultValues.set('HIHosAdmissionRoutineNewBornBenefit.maxLimit', 1);
            defaultValues.set(
              'HIConfinementRoutineNewBornBenefit.benefitAmount',
              fromJS({_type: 'Money', amount: 25, currency: 'USD'})
            );
            defaultValues.set('HIConfinementRoutineNewBornBenefit.confinementLimit', situsStateCd === 'AR' ? 5 : 3);
          }
        }

        defaultValues.set(PortabilityAppliedIndicator, isPortabilityApplied);
        defaultValues.set('HIBenefitStructure.isHosAdmissionApplied', true);
        defaultValues.set('HIBenefitStructure.isHosConfinementApplied', true);
        defaultValues.set('HIBenefitStructure.isDailyIntensiveCareConfinementApplied', true);
        defaultValues.set('HIBenefitStructure.isDailyStepDownIntensiveCareConfinmentApplied', true);

        // ObservationUnit will not be applied for NJ and ID states
        defaultValues.set('HIBenefitStructure.isObservationUnitApplied', !['NJ', 'ID'].includes(situsStateCd));
      })
    : ImmutableMap<string, any>();
};

const updatedDefaultsForGAI7 = (
  defaultValuesMap: ImmutableMap<string, any> | undefined,
  situsStateCd?: string,
  isPortabilityApplied?: boolean
) => {
  return isDefined(defaultValuesMap) && ImmutableMap.isMap(defaultValuesMap)
    ? defaultValuesMap.withMutations(defaultValues => {
        defaultValues.set('ACCBenefitStructure.isPainManagementCorticosteroidInjectionApplied', false);

        defaultValues.set('ACCBenefitStructure.isAutomobileAccidentalDeathApplied', false);

        defaultValues.set('ACCBenefitStructure.unitsOfDislocationRider', 2);
        defaultValues.set('ACCBenefitStructure.isEmergencyRoomServicesApplied', true);
        defaultValues.set('ACCBenefitStructure.isXRayApplied', true);
        defaultValues.set('ACCBenefitStructure.isFollowUpTreatmentApplied', true);
        defaultValues.set('ACCBenefitStructure.isDislocationAndFractureRiderBenefitApplied', true);
        defaultValues.set('ACCDislocationBenefit.isDislocationApplied', true);
        defaultValues.set('ACCFractureBenefit.isFractureApplied', true);
        defaultValues.set(
          'ACCBenefitStructure.isHealthScreeningServicesRiderApplied',
          isStateApplicableForHSS(situsStateCd)
        );

        defaultValues.set('ACCHealthScreeningServicesRider.healthScreeningType', 'Fixed');
        defaultValues.set('ACCOrganizedSportsActivityInjuryRider.benefitPct', 0.25);
        defaultValues.set('ACCOrganizedSportsActivityInjuryRider.isAccidentFacilityCareApplied', true);
        defaultValues.set('ACCOrganizedSportsActivityInjuryRider.isAccidentInjuriesApplied', true);
        defaultValues.set('ACCOrganizedSportsActivityInjuryRider.isAccidentTreatmentUrgentCareApplied', true);
        defaultValues.set('ACCOrganizedSportsActivityInjuryRider.isTreatmentSupportRecoveryApplied', true);
        defaultValues.set('ACCOrganizedSportsActivityInjuryRider.isDislocationFractureRiderApplied', true);
        defaultValues.set('ACCBenefitStructure.isSpinalManipulationServicesApplied', situsStateCd === 'KS');
        defaultValues.set('ACCBenefitStructure.isOrganizedSportsActivityInjuryRiderApplied', true);
        defaultValues.set('ACCHealthScreeningServicesRider.healthScreeningHashUnits', 2);
        defaultValues.set('ACCHealthScreeningServicesRider.timePeriodCd', 'Calender Year');
        defaultValues.set('ACCHealthScreeningServicesRider.benefitAmount', getMoneyObject(25));
        defaultValues.set('ACCHealthScreeningServicesRider.isPreventativeEnabled', false);
        defaultValues.set('ACCBenefitStructure.unitsOfAccidentFacilityCare', 2);
        defaultValues.set('ACCBenefitStructure.unitsOfFractureRider', 2);
        defaultValues.set('ACCBenefitStructure.unitsOfAccidentInjuries', 2);
        defaultValues.set('ACCBenefitStructure.unitsOfAccAmbulanceUrgentCare', 2);
        defaultValues.set('ACCBenefitStructure.unitsOfAccAccidentSurgeries', 2);
        defaultValues.get('ACCBenefitStructure.unitsOfDiagnosticPainManagement', 2);

        defaultValues.set('ACCBenefitStructure.unitsOfTreatmentSupportAndRecovery', 2);
        defaultValues.set('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment', 2);

        defaultValues.set('ACCBenefitStructure.unitsOfEmergencyRoomServices', 2);
        defaultValues.set('ACCXRayBenefit.units', 2);
        defaultValues.set('ACCFollowUpTreatmentBenefit.units', 2);
        defaultValues.set('ACCBenefitStructure.isAccidentalDeathApplied', true);
        defaultValues.set('ACCBenefitStructure.isAccidentalDeathCommonCarrierApplied', true);
        defaultValues.set('ACCBenefitStructure.isAccidentalDismembermentApplied', true);
        defaultValues.set('ACCBenefitStructure.isFunctionalLossApplied', true);

        defaultValues.set('ACCBenefitStructure.isCompanionNonLocalLodgingApplied', true);
        defaultValues.set('ACCBenefitStructure.isMedicalEquipmentApplied', true);
        defaultValues.set('ACCBenefitStructure.isMedicalSuppliesApplied', true);
        defaultValues.set('ACCBenefitStructure.isMedicationApplied', true);
        defaultValues.set('ACCBenefitStructure.isNonLocalTransportationApplied', true);
        defaultValues.set('ACCBenefitStructure.isPostAccidentCommonCarrierTransportationApplied', true);
        defaultValues.set('ACCBenefitStructure.isProsthesisApplied', true);
        defaultValues.set('ACCBenefitStructure.isResidenceAutomobileModificationApplied', true);
        defaultValues.set('ACCBenefitStructure.isTherapyApplied', true);
        defaultValues.set('ACCBenefitStructure.isMajorDiagnosticExamApplied', true);
        defaultValues.set('ACCBenefitStructure.isPainManagementApplied', true);
        defaultValues.set('ACCBenefitStructure.epiduralInjection', true);

        defaultValues.set('ACCBenefitStructure.isMiscellaneousOutpatientSurgeryApplied', true);
        defaultValues.set('ACCBenefitStructure.isOpenAbdominalOrThoracicSurgeryApplied', true);
        defaultValues.set('ACCBenefitStructure.isTendonLigamentRotatorCuffApplied', true);
        defaultValues.set('ACCBenefitStructure.isGeneralAnesthesiaApplied', true);
        defaultValues.set('ACCBenefitStructure.isRupturedDiscApplied', true);
        defaultValues.set('ACCBenefitStructure.isBloodPlasmaPlateletsApplied', true);
        defaultValues.set('ACCBenefitStructure.isEyeInjuryApplied', true);

        defaultValues.set('ACCBenefitStructure.isAccidentPhysicianTreatmentApplied', true);
        defaultValues.set('ACCBenefitStructure.isAmbulanceAirApplied', true);
        defaultValues.set('ACCBenefitStructure.isAmbulanceGroundOrWaterApplied', true);
        defaultValues.set('ACCBenefitStructure.isUrgentCareApplied', true);
        defaultValues.set('ACCBenefitStructure.isBrainInjuryDiagnosisApplied', true);
        defaultValues.set('ACCBenefitStructure.isBrokenToothApplied', true);
        defaultValues.set('ACCBenefitStructure.isBurnApplied', true);
        defaultValues.set('ACCBenefitStructure.isComaApplied', situsStateCd !== 'GA');
        defaultValues.set('ACCBenefitStructure.isLacerationApplied', true);
        defaultValues.set('ACCBenefitStructure.isParalysisApplied', true);
        defaultValues.set('ACCBenefitStructure.isHospitalAdmissionApplied', true);

        defaultValues.set('ACCBenefitStructure.isHospitalConfinementApplied', true);

        defaultValues.set('ACCBenefitStructure.isIntensiveCareUnitConfinementApplied', true);
        defaultValues.set('ACCBenefitStructure.isBurnSkinGraftApplied', true);
        defaultValues.set('ACCHealthScreeningServicesRider.timePeriodCd', 'CalendarYear');
        defaultValues.set('ACCBenefitStructure.isPunctureWoundApplied', false);
        defaultValues.set('ACCBenefitStructure.isGunshotWoundApplied', false);
        defaultValues.set('ACCBenefitStructure.isPortabilityApplied', true);
        defaultValues.set('ACCExclOnTheJobAccident.isExclusion', false);
        defaultValues.set('ACCBenefitStructure.isRehabilitationFacilityApplied', true);
        defaultValues.set('ACCDislocationBenefit.partialDislocation', '0.25');
      })
    : ImmutableMap<string, any>();
};

const updatedDefaultsForGVA6 = (
  defaultValuesMap: ImmutableMap<string, any> | undefined,
  situsStateCd?: string,
  isPortabilityApplied?: boolean
) => {
  return isDefined(defaultValuesMap) && ImmutableMap.isMap(defaultValuesMap)
    ? defaultValuesMap.withMutations(defaultValues => {
        defaultValues.set('ACCBenefitStructure.unitsOfAccidentFacilityCare', 2);
        defaultValues.set('ACCBenefitStructure.isHospitalAdmissionApplied', true);
        defaultValues.set('ACCBenefitStructure.isHospitalConfinementApplied', true);
        defaultValues.set('ACCBenefitStructure.isIntensiveCareUnitConfinementApplied', true);
        defaultValues.set('ACCBenefitStructure.isObjectiveSecondOpinionApplied', situsStateCd == 'MD');

        defaultValues.set('ACCBenefitStructure.unitsOfAccAmbulanceUrgentCare', 2);
        defaultValues.set('ACCBenefitStructure.isAccidentPhysicianTreatmentApplied', true);
        defaultValues.set('ACCBenefitStructure.isAmbulanceAirApplied', true);
        defaultValues.set('ACCBenefitStructure.isAmbulanceGroundOrWaterApplied', true);
        defaultValues.set('ACCBenefitStructure.isUrgentCareApplied', true);
        defaultValues.set('ACCBenefitStructure.isXRayApplied', true);

        defaultValues.set('ACCBenefitStructure.isEmergencyRoomServicesApplied', true);
        defaultValues.set('ACCEmergencyRoomServicesBenefit.unitsOfEmergencyRoomServices', 2);

        defaultValues.set('ACCBenefitStructure.unitsOfAccidentInjuries', 2);
        defaultValues.set('ACCBenefitStructure.scheduleOfBenefitEnhancementRider', 'Standard');
        defaultValues.set('ACCBenefitStructure.isBrainInjuryDiagnosisApplied', true);
        defaultValues.set('ACCBenefitStructure.isBrokenToothApplied', true);
        defaultValues.set('ACCBenefitStructure.isBurnApplied', true);
        defaultValues.set('ACCBenefitStructure.isBurnSkinGraftApplied', true);
        defaultValues.set('ACCBenefitStructure.isComaApplied', true);
        defaultValues.set('ACCBenefitStructure.isLacerationApplied', true);
        defaultValues.set('ACCBenefitStructure.isParalysisApplied', true);
        defaultValues.set('ACCBenefitStructure.isRehabilitationFacilityApplied', true);
        defaultValues.set('ACCBenefitStructure.isFollowUpTreatmentApplied', true);
        defaultValues.set('ACCBenefitStructure.isMiscellaneousOutpatientSurgeryApplied', true);
        defaultValues.set('ACCBenefitStructure.isOpenAbdominalOrThoracicSurgeryApplied', true);
        defaultValues.set('ACCBenefitStructure.isTendonLigamentRotatorCuffApplied', true);
        defaultValues.set('ACCBenefitStructure.isGeneralAnesthesiaApplied', true);
        defaultValues.set('ACCBenefitStructure.isRupturedDiscApplied', true);
        defaultValues.set('ACCBenefitStructure.isBloodPlasmaPlateletsApplied', true);
        defaultValues.set('ACCBenefitStructure.isEyeInjuryApplied', true);
        defaultValues.set('ACCBenefitStructure.isMajorDiagnosticExamApplied', true);
        defaultValues.set('ACCBenefitStructure.isPainManagementApplied', true);
        defaultValues.set('ACCBenefitStructure.epiduralInjection', true);
        defaultValues.set('ACCBenefitStructure.isCompanionNonLocalLodgingApplied', true);
        defaultValues.set('ACCBenefitStructure.isMedicalEquipmentApplied', true);
        defaultValues.set('ACCBenefitStructure.isMedicalSuppliesApplied', true);
        defaultValues.set('ACCBenefitStructure.isMedicationApplied', true);
        defaultValues.set('ACCBenefitStructure.isNonLocalTransportationApplied', true);
        defaultValues.set('ACCBenefitStructure.isPostAccidentCommonCarrierTransportationApplied', true);
        defaultValues.set('ACCBenefitStructure.isProsthesisApplied', true);
        defaultValues.set('ACCBenefitStructure.isResidenceAutomobileModificationApplied', true);
        defaultValues.set('ACCBenefitStructure.isTherapyApplied', true);

        defaultValues.set('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment', 2);
        defaultValues.set('ACCBenefitStructure.isAccidentalDeathApplied', true);
        defaultValues.set('ACCBenefitStructure.isAccidentalDeathCommonCarrierApplied', true);
        defaultValues.set('ACCBenefitStructure.isAccidentalDismembermentApplied', true);
        defaultValues.set('ACCBenefitStructure.isFunctionalLossApplied', true);

        defaultValues.set('ACCBenefitStructure.isDislocationAndFractureRiderBenefitApplied', true);
        defaultValues.set('ACCBenefitStructure.unitsOfDislocationRider', 2);
        defaultValues.set('ACCDislocationFractureRiderDetails.scheduleOfDislocationRider', 'Enhanced');
        defaultValues.set('ACCDislocationFractureRiderDetails.timesMaximumClosedBenefit', 1);
        defaultValues.set('ACCDislocationFractureRiderDetails.timesMaximumOpenBenefit', 1);
        defaultValues.set('ACCDislocationFractureRiderDetails.timePeroidMaximumClosedBenefit', 'CoveredAccident');
        defaultValues.set('ACCDislocationFractureRiderDetails.timePeroidMaximumOpenBenefit', 'CoveredAccident');
        defaultValues.set('ACCDislocationBenefit.isDislocationApplied', true);
        defaultValues.set('ACCFractureBenefit.isFractureApplied', true);

        defaultValues.set('ACCBenefitStructure.isPortabilityApplied', true);
        defaultValues.set('ACCExclOnTheJobAccident.isExclusion', false);
      })
    : ImmutableMap<string, any>();
};

const updatedDefaultsForGPLU = (defaultValuesMap: ImmutableMap<string, any> | undefined, situsStateCd?: string) => {
  return isDefined(defaultValuesMap) && ImmutableMap.isMap(defaultValuesMap)
    ? defaultValuesMap.withMutations(defaultValues => {
         Object.values(GPLUCoverageCode).forEach(value => {
            defaultValues.set(value, updateDefaultsForCoverage(value, defaultValues.get(value), situsStateCd));
         });
      })
    : ImmutableMap<string, any>();
};

const updateDefaultsForCoverage = (coverageName: string, defaultValuesMap: ImmutableMap<string, any>, situsStateCd?: string) => {
  switch(coverageName) {
    case GPLUCoverageCode.WHOLE_LIFE:
      return defaultValuesMap?.withMutations(defaultValues => {
        const benefitMinAmountObj = defaultValues.get('GPLUBenefitScheduleRangeValues.benefitMinAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValues.benefitMinAmount', fromJS({
          amount: benefitMinAmountObj?.amount || 0, 
          currency: 'USD'
        }));
        const incrementAmountObj = defaultValues.get('GPLUBenefitScheduleRangeValues.incrementAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValues.incrementAmount', fromJS({
          amount: incrementAmountObj?.amount || 0, 
          currency: 'USD'
        }));
        const maxAcceleratedBenefitAmount = defaultValues.get('GPLUAcceleratedBenefitOptions.maxAcceleratedBenefitAmount');
        defaultValues.set('GPLUAcceleratedBenefitOptions.maxAcceleratedBenefitAmount', fromJS({
          amount: maxAcceleratedBenefitAmount?.amount || 0, 
          currency: 'USD'
        }));
      });
    case GPLUCoverageCode.SPOUSE_WHOLE_LIFE:
      return defaultValuesMap?.withMutations(defaultValues => {
        defaultValues.set('GPLUUnderwriting.underwritingTypeCd', 'GI');
        defaultValues.set('GPLUBenefitSchedulePctOfEmployeeAmount.employeeAmountPct', situsStateCd === 'NE' ? 0.5 : 1);
        defaultValues.set('GPLUSpouseUnderwritingAmountsWs.guaranteedIssueAmount', fromJS({_type: 'Money', amount: 30000, currency: 'USD'}));
        defaultValues.set('GPLUSpouseUnderwritingAmountsNws.guaranteedIssueAmount', fromJS({_type: 'Money', amount: 10000, currency: 'USD'}));
        const incrementAmountObjWs = defaultValues.get('GPLUBenefitScheduleRangeValuesWs.incrementAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValuesWs.incrementAmount', fromJS({
          _type: 'Money', 
          amount: incrementAmountObjWs?.amount || 0, 
          currency: 'USD'
        }));
        const benefitMinAmountObjWs = defaultValues.get('GPLUBenefitScheduleRangeValuesWs.benefitMinAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValuesWs.benefitMinAmount', fromJS({
          _type: 'Money', 
          amount: benefitMinAmountObjWs?.amount || 0, 
          currency: 'USD'
        }));
        const incrementAmountObjNws = defaultValues.get('GPLUBenefitScheduleRangeValuesNws.incrementAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValuesNws.incrementAmount', fromJS({
          _type: 'Money', 
          amount: incrementAmountObjNws?.amount || 0, 
          currency: 'USD'
        }));
        const benefitMinAmountObjNws = defaultValues.get('GPLUBenefitScheduleRangeValuesNws.benefitMinAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValuesNws.benefitMinAmount', fromJS({
          _type: 'Money', 
          amount: benefitMinAmountObjNws?.amount || 0, 
          currency: 'USD'
        }));
      });
    case GPLUCoverageCode.CHILD_TERM_LIFE:
      return defaultValuesMap?.withMutations(defaultValues => {
        defaultValues.set('GPLUUnderwriting.guaranteedIssueAmount', fromJS({
          _type: 'Money', 
          amount: situsStateCd !== 'PR' ? 10000 : 2000, 
          currency: 'USD'
        }));
        defaultValues.set('GPLUBenefitSchedulePctOfEmployeeAmount.employeeAmountPct', 0.5);
        const benefitMinAmountObj = defaultValues.get('GPLUBenefitScheduleRangeValues.benefitMinAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValues.benefitMinAmount', fromJS({
          _type: 'Money', 
          amount: situsStateCd !== 'PR' ? benefitMinAmountObj?.amount : 2000,
          currency: 'USD'
        }));
        const incrementAmountObj = defaultValues.get('GPLUBenefitScheduleRangeValues.incrementAmount');
        defaultValues.set('GPLUBenefitScheduleRangeValues.incrementAmount', fromJS({
          _type: 'Money', 
          amount: incrementAmountObj?.amount || 0,
          currency: 'USD'
        }));
      });
    case GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE:
      return defaultValuesMap?.withMutations(defaultValues => {
        defaultValues.set('GPLUBenefitScheduleLTCChronic.benefitTypeLTCChronicCd', 'AccelDeathBenefitAmount');
        //temp. To be removed when PnR provides the value
        defaultValues.set('GPLUBenefitScheduleLTCChronic.monthlyDeathBenefitPct', 0.04);
        //temp. To be removed when PnR provides the value
        defaultValues.set('GPLUBenefitScheduleLTCChronic.maxBenefitTimePeriod', 50);

        // temp. To be removed when PnR provides the value
        defaultValues.set('GPLUPreExLimitation.preExConditionLimitationCd', 'PreEx');
        defaultValues.set('GPLUPreExLimitation.lookBackPeriodCd', '6');
        defaultValues.set('GPLUPreExLimitation.preExConditionLimitationPeriodCd', '6');

        //temp. To be removed when PnR provides the value
        if (situsStateCd === 'TX') defaultValues.set('GPLUPreExLimitation.preExConditionLimitationCd', 'NoPreEx');

        // Exclusions 
        defaultValues.set('GPLUExclusions.hasMentalOrEmotionalDisorder', true);
        defaultValues.set('GPLUExclusions.isForeignTreatment', true);
        defaultValues.set('GPLUExclusions.isSuicideOrSelfInflictedInjury', true);
        defaultValues.set('GPLUExclusions.hasAlcoholOrDrugAddiction', true);
        defaultValues.set('GPLUExclusions.isServicePayableUnderGovtProgram', true);
        defaultValues.set('GPLUExclusions.isWarIllness', true);
        defaultValues.set('GPLUExclusions.isCoveredUnderWorkersCompOrNoFaultLaw', true);
      });
  }
};
